import { useMemo } from 'react';

import type {
  CustomAttributeSchemaInput,
  TableFields,
  TableRecord,
} from '../types';
import type { CustomAttributeAlias } from '../utils/coalesceCustomAttributeFields';
import {
  getAliasedPaths,
  NO_ALIASES,
} from '../utils/coalesceCustomAttributeFields';
import {
  convertSchemasToFieldConfigs,
  toCustomAttributeSchemas,
} from '../utils/customAttributes';
import type { TableFieldsWithCustomAttributes } from './useAddCustomAttributeFieldData';

type Options<T extends TableRecord> = {
  customAttributeSchema: CustomAttributeSchemaInput;
  fields: TableFields<T>;
  enableRelativeDates: boolean;
  /** Only the primary path of each group keeps a column. */
  aliases?: CustomAttributeAlias[];
  // Bare schema property paths whose field config is Hidden — excluded from
  // register columns and property filters (CUS-216).
  hiddenPaths?: Set<string>;
};

/**
 * Add custom attributes field definitions
 *
 * @param options
 * @returns
 */
export const useAddCustomAttributeFieldDefinitions = <T extends TableRecord>({
  customAttributeSchema,
  fields,
  enableRelativeDates,
  aliases = NO_ALIASES,
  hiddenPaths,
}: Options<T>): TableFieldsWithCustomAttributes<T> => {
  return useMemo(() => {
    const customFields = convertSchemasToFieldConfigs({
      customAttributeSchemas: toCustomAttributeSchemas(customAttributeSchema),
      enableRelativeDates,
      hiddenPaths,
    });

    const aliasedPaths = getAliasedPaths(aliases);
    if (aliasedPaths.size === 0) {
      return {
        ...fields,
        ...customFields,
      };
    }

    // Drop the duplicate columns, keeping each group's primary. `_alt` variants
    // are keyed `${path}_alt` and follow their base path.
    const surviving: typeof customFields = {};
    for (const [key, config] of Object.entries(customFields)) {
      const basePath = key.endsWith('_alt')
        ? key.slice(0, -'_alt'.length)
        : key;
      if (aliasedPaths.has(basePath)) {
        continue;
      }
      surviving[key] = config;
    }

    return {
      ...fields,
      ...surviving,
    };
  }, [
    customAttributeSchema,
    enableRelativeDates,
    fields,
    aliases,
    hiddenPaths,
  ]);
};
